/**
 * D2 -- an IBS's tolerance, said the same way everywhere.
 *
 * "2h" alone overstates it: it reads as a governed figure when in most
 * estates nobody has signed for it. "--" understates it: the number IS
 * in force and every breach verdict uses it. So the badge always says
 * which of the three it is.
 */
import { useTranslation } from "react-i18next";

import { useSeatHolders } from "shared/api/useSeatHolders";
import { toleranceSentence } from "shared/tolerance/toleranceSentence";
import type { ToleranceStateRow } from "shared/tolerance/useToleranceState";

const TONE: Record<string, string> = {
  signed: "border-rag-greenEdge bg-rag-greenBg text-rag-green",
  set_unsigned: "border-amber-300 bg-amber-50 text-amber-800",
  // C1 -- work has been done and is waiting on somebody else. Not
  // green (nothing is in force) and not grey (it is not absent).
  pending: "border-sky-300 bg-sky-50 text-sky-800",
  undeclared: "border-slate-300 bg-slate-50 text-slate-500",
};

export function ToleranceBadge({
  state,
  className,
}: {
  state: ToleranceStateRow | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  /* ROUND 13 G7 -- the sentence now comes from `toleranceSentence`,
     which the owner's declaration form and the incident strip call
     too. Eight rounds of this finding were each closed by making the
     files agree again, and each time a further surface was written
     that did not know. One wording because one function.

     Not knowing is not the same as "undeclared", so a row the helper
     cannot read renders nothing rather than asserting an absence. */
  // ROUND 15 G2 -- the holder's name, where the badge waits on a seat.
  const seats = useSeatHolders(Boolean(state?.awaiting_role));
  const label = toleranceSentence(t, state, seats.data);
  if (!state || label === null || !(state.status in TONE)) return null;
  const lapsed = (state.signer_seat_lapsed ?? []).length > 0;
  return (
    <span
      data-testid="tolerance-badge"
      data-status={state.status}
      // Round 20 V2 -- a signature whose signer has left the seat is not
      // the green "declared and signed" of a governed figure.
      data-seat-lapsed={lapsed ? "true" : undefined}
      title={state.signed_by ?? undefined}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${
        lapsed
          ? (TONE.set_unsigned ?? TONE.undeclared)
          : (TONE[state.status] ?? TONE.undeclared)
      } ${className ?? ""}`.trim()}
    >
      {label}
    </span>
  );
}
